// Contrat des skins coureur (style « Cartoon 24×28 » validé le 19/07, spec
// docs/superpowers/specs/2026-07-19-skins-cartoon-design.md).
// Source des grilles : scripts/cartoon-skins.mjs (ordres verrouillés, test de synchro).
// Feuille : public/assets/runners.png — cellules 32×32, art 24×28 posé à (4,4)
// (mêmes proportions dans la cellule que l'ancien 12×14 dans 16×16 : la
// destination écran TS×TS ne change pas).

// Roster RÉDUIT à un seul héros le 19/07 (décision Claude-Alain) : « Le Baroudeur »
// remplace le modèle du jeu ; les 10 explorateurs cartoon sont retirés du choix (leurs
// grilles restent en code mort dans cartoon-skins.mjs, jamais cuites). L'id 'standard' est
// CONSERVÉ (pas de migration DB : les comptes ayant choisi un ancien skin retombent sur
// l'index 0 via skinIndex → Le Baroudeur). Seule la teinte de tenue reste choisie (7 couleurs).
export const SKIN_IDS = ['standard'] as const
export type SkinId = (typeof SKIN_IDS)[number]

export const SKIN_LABELS: Record<SkinId, string> = {
  standard: 'Le Baroudeur',
}

// Palette de teintes de la TENUE (le 1er = couleur d'origine). Historique :
// vivait dans client/prefs.ts, déménagée ici pour être partagée avec le serveur
// (validation) et le générateur (pré-cuisson des 7 variantes par skin).
export const AVATAR_HUES = [-1, 150, 90, 300, 40, 200, 260] as const

// Ordre des colonnes de la feuille runners.png. Miroir exact de POSES dans
// scripts/cartoon-skins.mjs (verrouillé par tests/skins-sheet.test.ts).
export const POSES = [
  'idle', 'run0', 'run1', 'climb0', 'climb1', 'rope0', 'rope1', 'fall',
  'dig', 'crouch', 'dash0', 'dash1', 'brake', 'pickUp', 'pickHit',
  'tramp0', 'tramp1', 'tramp2',
] as const
export type PoseId = (typeof POSES)[number]

export const RUNNER_CELL = 32
// Colonnes 18-19 et 20-21 : smears de dash (2 cellules de large chacun, dérivés
// de dash0/dash1 par étirement ×2). Dernière ligne : glace partagée (cols 0-1).
export const RUNNERS_SHEET_COLS = 22
export const RUNNERS_SHEET_ROWS = SKIN_IDS.length * AVATAR_HUES.length + 1

export function skinIndex(id: string): number {
  const i = (SKIN_IDS as readonly string[]).indexOf(id)
  return i < 0 ? 0 : i
}
export function hueIndex(hue: number): number {
  const i = (AVATAR_HUES as readonly number[]).indexOf(hue)
  return i < 0 ? 0 : i
}

// Rect source (coin haut-gauche, cellule 32×32) d'une pose pour un (skin, teinte).
export function runnerRef(skinIdx: number, hueIdx: number, poseIdx: number): { sx: number; sy: number } {
  return { sx: poseIdx * RUNNER_CELL, sy: (skinIdx * AVATAR_HUES.length + hueIdx) * RUNNER_CELL }
}
// Smears (frame 0 ou 1) : 2 cellules de large.
export function smearRef(skinIdx: number, hueIdx: number, frame: 0 | 1): { sx: number; sy: number } {
  return { sx: (18 + frame * 2) * RUNNER_CELL, sy: (skinIdx * AVATAR_HUES.length + hueIdx) * RUNNER_CELL }
}
// Glace du burst (partagée, non teintée) : dernière ligne, cols 0-1.
export function iceRef(frame: 0 | 1): { sx: number; sy: number } {
  return { sx: frame * RUNNER_CELL, sy: SKIN_IDS.length * AVATAR_HUES.length * RUNNER_CELL }
}

// --- Multi : apparence transmise par le serveur -------------------------------------------
// pet / title (Mine C7) : cosmétiques ÉQUIPÉS du compte, joints à l'apparence pour être visibles
// des AUTRES joueurs en ligne (pet dessiné derrière chaque coureur, titre montré à l'écran de fin).
// OPTIONNELS : un compte sans pet/titre équipé (ou un vieux serveur) n'envoie simplement pas la clé.
// Ce sont des loot_id bruts (« pet-chat », « titre-mineur ») ; le client les résout via le catalogue.
// outfit (26/07) : la TENUE équipée, transmise comme pet/title pour que les autres joueurs la
// voient. OPTIONNELLE : un compte sans tenue (ou un vieux serveur) n'envoie simplement pas la clé.
export interface PlayerLook { skin: string; hue: number; pet?: string; title?: string; outfit?: string; hat?: string; pickaxe?: string }
// Anti-jumeaux (garde-fou des maquettes du vestiaire) : deux joueurs au même couple
// (skin, teinte) seraient indistinguables en partie. Slot par slot : le premier garde son
// choix, les suivants avancent leur teinte au prochain cran LIBRE de AVATAR_HUES pour CE
// skin (déterministe : même entrée → même sortie, des deux côtés du réseau).
export function dedupLooks(looks: readonly PlayerLook[]): PlayerLook[] {
  const hues = AVATAR_HUES as readonly number[]
  const taken = new Set<string>()
  return looks.map((l) => {
    let hue = l.hue
    if (taken.has(l.skin + '/' + hue)) {
      const start = Math.max(0, hues.indexOf(hue))
      for (let step = 1; step <= hues.length; step++) {
        const cand = hues[(start + step) % hues.length]!
        if (!taken.has(l.skin + '/' + cand)) { hue = cand; break }
      }
    }
    taken.add(l.skin + '/' + hue)
    // `{ ...l, hue }` (et non `{ skin: l.skin, hue }`) : SEULE la teinte est éventuellement
    // décalée ; tout autre champ (pet, title, futurs cosmétiques) est PRÉSERVÉ tel quel.
    return { ...l, hue }
  })
}
